import { TextSelection } from '@atlaskit/editor-prosemirror/state';
import { Box, xcss } from '@atlaskit/primitives';
import { getDatabaseContainerType, useAnalytics } from '@databases/analytics';
import {
  InlineRovoPromptTriggerSource,
  openInlineRovoPrompt
} from '@databases/database-ai-first-creation-state/inline-rovo-prompt-state';
import { AskRovoIcon } from '@databases/database-ai-first-creation-ui/icons/ask-rovo-icon';
import { defaultStoreUtils } from '@databases/redux';
import { selectFieldIdsByContentId } from '@databases/redux/database-view';
import { getService } from '@databases/service-container';
import useHandleOutsideClick from '@databases/utils/hooks/useHandleOutsideClick';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { type ADFDocument } from '@canvas/adf';
import { getAdfTextContentWithUrl } from '@canvas/adf/get-adf-text-content';
import { resolveRichTextValue } from '@canvas/adf/resolve-rich-text-value';
import {
  DatabasesEditor,
  type DatabasesEditorAPI
} from '@canvas/editor/databases-editor';
import { DatabasesFormattingToolbar } from '@canvas/editor/databases-formatting-toolbar';

import { type TextLikeFieldType } from '../types';

import { TEXT_VALUE_MAX_TEXT_AREA_HEIGHT } from './popover-edit-common';
import { createRichTextChange } from './text-value-change';
import { type EditComponentProps } from './types';

const POPUP_EXCLUDED_SELECTORS = [
  '[role="menu"]',
  '[role="listbox"]',
  '[role="dialog"]',
  '[data-placement]',
  '[data-ds--level]'
];

const textAreaContainerStyling = xcss({
  display: 'flex',
  flexDirection: 'row',
  boxSizing: 'border-box',
  position: 'relative'
});

const bulkEditContainerStyling = xcss({
  display: 'flex',
  flexDirection: 'column',
  boxSizing: 'border-box',
  position: 'relative'
});

const lastFieldStylings = xcss({
  flexDirection: 'row-reverse'
});

const textAreaMinHeightStyling = xcss({
  minHeight: 'size.400'
});

const editorWrapperBaseStyles = xcss({
  borderWidth: 'border.width.selected',
  borderStyle: 'solid',
  borderColor: 'color.border.selected',
  borderRadius: 'radius.small',
  backgroundColor: 'elevation.surface',
  overflow: 'auto',
  boxSizing: 'border-box',
  maxHeight: `${TEXT_VALUE_MAX_TEXT_AREA_HEIGHT}px`,
  font: 'font.body',
  width: '100%'
});

const editorTablePadding = xcss({
  paddingTop: 'space.100',
  paddingRight: 'space.075',
  paddingBottom: 'space.100'
});

const editorTableStyles = {
  // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
  paddingLeft: '10px',
  minHeight: '100%',
  maxHeight: 'none'
} as const;

const editorSidePanelPadding = xcss({
  paddingBlock: 'space.100',
  paddingInline: 'space.075'
});

const ASK_ROVO_ICON_SIZE = 16;

const MARK_TYPE_MAP: Record<string, string> = {
  strong: 'bold',
  em: 'italic',
  underline: 'underline',
  strike: 'strikethrough',
  code: 'code',
  subsup: 'subsup',
  textColor: 'textColor',
  backgroundColor: 'highlightColor',
  link: 'link'
};

const NODE_TYPE_SET = new Set(['bulletList', 'orderedList']);

/**
 * Walks an ADF tree and collects all formatting types present.
 * Checks both marks (bold, italic, link, etc.) and node types (lists).
 */
function extractFormattingTypes(adf: ADFDocument | undefined): Set<string> {
  const types = new Set<string>();
  if (!adf) {
    return types;
  }

  function walk(node: Record<string, unknown>) {
    if (Array.isArray(node.marks)) {
      for (const mark of node.marks) {
        const mapped = MARK_TYPE_MAP[(mark as { type: string }).type];
        if (mapped) {
          types.add(mapped);
        }
      }
    }
    if (typeof node.type === 'string' && NODE_TYPE_SET.has(node.type)) {
      types.add(node.type);
    }
    if (Array.isArray(node.content)) {
      for (const child of node.content) {
        walk(child as Record<string, unknown>);
      }
    }
  }

  walk(adf as unknown as Record<string, unknown>);
  return types;
}

const inlineToolbarWrapperStyles = xcss({
  marginBlockStart: 'space.050',
  width: 'fit-content'
});

const floatingToolbarWrapperStyles = xcss({
  position: 'absolute',
  insetInlineStart: 'space.0',
  top: '100%',
  marginBlockStart: 'space.050',
  zIndex: 'blanket'
});

function RichTextValuePopoverEdit<Type extends TextLikeFieldType>({
  field,
  onChange,
  onFocus,
  onBlur,
  autoFocus,
  value,
  contentId,
  entryId,
  isRenderedInSidePanel,
  isBulkEdit,
  ariaLabel,
  'aria-describedby': ariaDescribedBy,
  placeholder,
  initialKey
}: EditComponentProps<Type>) {
  const analyticsClient = useAnalytics();
  const databaseContainer = getDatabaseContainerType();

  const initialFormattingRef = useRef<Set<string> | null>(null);
  if (initialFormattingRef.current === null) {
    initialFormattingRef.current = extractFormattingTypes(resolveRichTextValue(value));
  }
  const latestAdfRef = useRef<ADFDocument | undefined>(resolveRichTextValue(value));
  const hasFiredAnalyticsRef = useRef(false);

  const fireFormattingAnalytics = useCallback(() => {
    if (hasFiredAnalyticsRef.current) {
      return;
    }
    hasFiredAnalyticsRef.current = true;

    const finalTypes = extractFormattingTypes(latestAdfRef.current);
    for (const formattingType of finalTypes) {
      if (!initialFormattingRef.current?.has(formattingType)) {
        analyticsClient.sendRichTextFormattingAppliedEvent({
          contentId,
          databaseContainer,
          formattingType
        });
      }
    }
  }, [analyticsClient, contentId, databaseContainer]);

  const onBlurRef = useRef(onBlur);
  const selectionsActions = getService('SelectionsActions');
  onBlurRef.current = onBlur;
  // handleBlur must stay stable (empty deps) so DatabasesEditor does not re-render on every parent render.
  // The ref lets us always invoke the latest onBlur from the stable callback, and on unmount cleanup.
  // We ignore blur events entirely and instead rely on mousedown-outside detection below,
  // because toolbar dropdown portals cause spurious blur events.
  const handleBlur = useCallback((_e: React.FocusEvent) => {
    // no-op — closing is handled by the mousedown listener below
  }, []);

  const containerRef = useRef<HTMLDivElement>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const handleOutsideClick = useCallback(() => {
    setHasEditorFocusRef.current?.(false);
    if (containerRef.current?.contains(document.activeElement)) {
      (document.activeElement as HTMLElement)?.blur?.();
    }

    onBlurRef.current?.();
  }, []);

  useHandleOutsideClick(
    handleOutsideClick,
    containerRef,
    POPUP_EXCLUDED_SELECTORS,
    !isDropdownOpen
  );

  useEffect(() => {
    return () => {
      fireFormattingAnalytics();
      onBlurRef.current?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const defaultValue = useMemo(() => {
    if (initialKey !== undefined) {
      return {
        type: 'doc',
        version: 1,
        content: [
          {
            type: 'paragraph',
            content: [{ type: 'text', text: initialKey }]
          }
        ]
      } as ADFDocument;
    }
    return resolveRichTextValue(value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = useCallback(
    (adf: ADFDocument) => {
      latestAdfRef.current = adf;
      const plain = getAdfTextContentWithUrl(adf, '');
      onChange({ plain, rich: adf }, createRichTextChange(field.type, adf));
    },
    [field.type, onChange]
  );

  const [editorApi, setEditorApi] = useState<DatabasesEditorAPI>(undefined);
  const [isLinkToolbarActive, setIsLinkToolbarActive] = useState(false);

  useEffect(() => {
    if (initialKey !== undefined) {
      const initialAdf = defaultValue as ADFDocument;
      onChange(
        { plain: initialKey, rich: initialAdf },
        createRichTextChange(field.type, initialAdf)
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (initialKey !== undefined && editorApi?.core?.actions.execute) {
      editorApi.core.actions.execute(({ tr }) => {
        tr.setSelection(TextSelection.atEnd(tr.doc));

        return tr;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editorApi]);

  // Subscribe to the hyperlink plugin shared state to detect when the link
  // floating toolbar is visible, so we can hide the formatting toolbar.
  useEffect(() => {
    if (!editorApi?.hyperlink) {
      return;
    }
    const unsubscribe = editorApi.hyperlink.sharedState.onChange(
      ({ nextSharedState }) => {
        setIsLinkToolbarActive(nextSharedState?.activeLinkMark != null);
      }
    );
    return unsubscribe;
  }, [editorApi]);

  const [hasEditorFocus, setHasEditorFocus] = useState(false);
  const setHasEditorFocusRef = useRef(setHasEditorFocus);
  setHasEditorFocusRef.current = setHasEditorFocus;

  // Track whether the editor has a non-empty text selection so we can show
  // the formatting toolbar only when text is highlighted.
  const [hasTextSelection, setHasTextSelection] = useState(false);
  useEffect(() => {
    const checkSelection = () => {
      const editorEl = containerRef.current?.querySelector('.ProseMirror');
      if (!editorEl) {
        return;
      }
      const pmViewDesc = (editorEl as any).pmViewDesc;
      if (pmViewDesc?.view) {
        const { state } = pmViewDesc.view;
        setHasTextSelection(!state.selection.empty);
        return;
      }
      // Fallback: use the browser's Selection API
      const sel = window.getSelection();
      if (sel && sel.rangeCount > 0 && editorEl.contains(sel.anchorNode)) {
        setHasTextSelection(!sel.isCollapsed);
      } else {
        setHasTextSelection(false);
      }
    };

    document.addEventListener('selectionchange', checkSelection);
    return () => document.removeEventListener('selectionchange', checkSelection);
  }, []);

  const isLastField = defaultStoreUtils.useEqualSelector((state) => {
    const fieldIds = selectFieldIdsByContentId(state, { contentId });
    return fieldIds.indexOf(field.id) === fieldIds.length - 1;
  });

  return (
    <Box
      ref={containerRef}
      testId={`rich-text-value-edit-expand-${isLastField ? 'left' : 'right'}`}
      xcss={[
        isBulkEdit ? bulkEditContainerStyling : textAreaContainerStyling,
        !isBulkEdit && isLastField && lastFieldStylings,
        isRenderedInSidePanel && textAreaMinHeightStyling
      ]}
    >
      <Box
        as="div"
        xcss={[
          editorWrapperBaseStyles,
          isRenderedInSidePanel ? editorSidePanelPadding : editorTablePadding
        ]}
        style={!isRenderedInSidePanel ? editorTableStyles : undefined}
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedBy}
        data-rich-text-edit-wrapper
      >
        <DatabasesEditor
          defaultValue={defaultValue}
          onChange={handleChange}
          onFocus={(e) => {
            setHasEditorFocus(true);
            onFocus?.(e as React.FocusEvent<HTMLElement>);
          }}
          onBlur={handleBlur}
          shouldFocus={autoFocus}
          placeholderText={placeholder}
          onEditorApi={setEditorApi}
        />
      </Box>
      {(!isRenderedInSidePanel || hasEditorFocus) &&
        (isBulkEdit || hasTextSelection || isDropdownOpen) &&
        !isLinkToolbarActive &&
        editorApi && (
          <Box
            xcss={isBulkEdit ? inlineToolbarWrapperStyles : floatingToolbarWrapperStyles}
            onMouseDown={(e: React.MouseEvent) => {
              // Prevent the cell from losing focus (and triggering deselectAll)
              // when interacting with the floating toolbar or its dropdowns.
              e.preventDefault();
            }}
          >
            <DatabasesFormattingToolbar
              editorApi={editorApi}
              onDropdownOpenChange={setIsDropdownOpen}
              hideAskRovo={isBulkEdit}
              askRovoIcon={
                <AskRovoIcon height={ASK_ROVO_ICON_SIZE} width={ASK_ROVO_ICON_SIZE} />
              }
              onAskRovo={() => {
                analyticsClient.sendRichTextFormattingAppliedEvent({
                  contentId,
                  databaseContainer,
                  formattingType: 'askRovo'
                });
                onBlurRef.current?.();
                if (entryId && !isRenderedInSidePanel) {
                  selectionsActions.selectValues([{ entryId, fieldId: field.id }], {
                    clear: true
                  });
                }
                openInlineRovoPrompt(InlineRovoPromptTriggerSource.RichTextToolbar);
              }}
            />
          </Box>
        )}
    </Box>
  );
}

export default RichTextValuePopoverEdit;
